import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle2, XCircle, RefreshCcw, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

/**
 * Iter152: Central Inbox – EIN zentrales Gmail-Postfach bedient alle Firmen.
 * User forwarden Belege von ihrer registrierten Business-Mail an diese Adresse.
 * Sender-Routing (owner → share → contact) ordnet die Firma automatisch zu.
 */
export default function CentralInboxSystemConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ email_address: "", app_password: "", label_filter: "", active: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/admin/central-inbox/config`);
      setCfg(r.data);
      if (r.data?.configured) {
        setForm({
          email_address: r.data.email_address || "",
          app_password: "",
          label_filter: r.data.label_filter || "",
          active: r.data.active !== false,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Config konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTest = async () => {
    if (!form.email_address || !form.app_password) {
      toast.error("Bitte E-Mail und App-Password eingeben.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiClient.post(`/admin/central-inbox/test`, {
        email_address: form.email_address,
        app_password: form.app_password,
      });
      setTestResult(r.data);
      if (r.data?.ok) toast.success("Verbindung OK");
      else toast.error(r.data?.detail || "Verbindungsfehler");
    } catch (e) {
      const detail = e.response?.data?.detail || "Test fehlgeschlagen";
      setTestResult({ ok: false, detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.email_address) {
      toast.error("Bitte Central-Inbox-Adresse eingeben.");
      return;
    }
    if (!cfg?.configured && !form.app_password) {
      toast.error("App-Password ist bei der Ersteinrichtung Pflicht.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email_address: form.email_address,
        label_filter: form.label_filter || null,
        active: form.active,
      };
      if (form.app_password) payload.app_password = form.app_password;
      const r = await apiClient.put(`/admin/central-inbox/config`, payload);
      setCfg(r.data);
      setForm((f) => ({ ...f, app_password: "" }));
      toast.success(cfg?.configured
        ? "Aktualisiert"
        : (form.active ? "Central Inbox aktiv — Polling alle 5 Min" : "Central Inbox gespeichert (Polling ist deaktiviert)"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm("Central-Inbox-Config wirklich löschen? Ab sofort werden keine Mails mehr gepollt.")) return;
    try {
      await apiClient.delete(`/admin/central-inbox/config`);
      setCfg({ configured: false });
      setForm({ email_address: "", app_password: "", label_filter: "", active: true });
      toast.success("Gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handlePollNow = async () => {
    setPolling(true);
    try {
      const r = await apiClient.post(`/admin/central-inbox/poll-now`);
      const d = r.data || {};
      toast.success(
        `✅ ${d.mails_processed || 0} Mails geprüft · ${d.matched || 0} zugeordnet · ` +
        `${d.ingested || 0} Anhänge · ${d.unmatched || 0} nicht zugeordnet · ${d.bounces_sent || 0} Bounces`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Poll fehlgeschlagen");
    } finally {
      setPolling(false);
    }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="central-inbox-config-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-rose-100 text-rose-700 border border-rose-200 flex items-center justify-center flex-shrink-0">
            <Mail className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Central Inbox (E-Mail-Belege für ALLE Firmen)</h3>
            <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mt-0.5">
              EIN zentrales Gmail-Postfach empfängt Belege für <em>alle</em> Firmen im System.
              Ein Nutzer forwardet aus seiner registrierten Business-Mail — das System matcht den
              Absender gegen Firmen-Inhaber, Mitarbeiter oder Kontakte und legt den Beleg
              automatisch in die richtige Firma. Bei unbekanntem Absender → freundliche Auto-Reply.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade …
        </div>
      ) : (
        <div className="space-y-3">
          {cfg?.configured && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="central-inbox-status">
              <div className="flex items-center gap-1.5 font-medium text-emerald-900">
                <CheckCircle2 className="h-4 w-4" /> Verknüpft — <span className="font-mono">{cfg.email_address}</span>
              </div>
              {cfg.last_polled_at && (
                <div className="text-emerald-800 mt-0.5">
                  Zuletzt geprüft: {new Date(cfg.last_polled_at).toLocaleString("de-CH")}
                </div>
              )}
              {cfg.last_stats && (
                <div className="text-emerald-800 mt-0.5">
                  Letzter Lauf: {cfg.last_stats.mails_processed} Mails · {cfg.last_stats.matched} zugeordnet ·
                  {" "}{cfg.last_stats.ingested} Anhänge · {cfg.last_stats.unmatched} unbekannt ·
                  {" "}{cfg.last_stats.bounces_sent} Bounces gesendet
                </div>
              )}
              {cfg.last_error && (
                <div className="text-rose-800 mt-0.5">⚠️ Letzter Fehler: {cfg.last_error}</div>
              )}
            </div>
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-900">
            <div className="font-semibold mb-1">📋 Setup in 3 Schritten:</div>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Kostenloses Gmail-Konto erstellen (z.B. <code className="bg-white px-1 rounded">belege.chbuchhaltung@gmail.com</code>)</li>
              <li>Bei diesem Gmail: <strong>2FA aktivieren</strong> + <strong>App-Password</strong> generieren (16 Zeichen)</li>
              <li>Adresse + App-Password hier eintragen. Nutzer kommunizieren die Adresse als &quot;Belege weiterleiten an: …&quot;</li>
            </ol>
            <div className="mt-2 border-t border-amber-200 pt-1.5">
              🔀 <strong>Later Migration zu Google Workspace:</strong> Alle Nutzer weiterhin an <code className="bg-white px-1 rounded">belege@ch-buchhaltung.ch</code> → hier nur Adresse + neues App-Password austauschen.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ci-email">Central-Inbox-Adresse</Label>
              <Input
                id="ci-email"
                type="email"
                placeholder="belege.chbuchhaltung@gmail.com"
                value={form.email_address}
                onChange={(e) => setForm({ ...form, email_address: e.target.value.trim() })}
                data-testid="central-inbox-email-input"
              />
            </div>
            <div>
              <Label htmlFor="ci-pwd">App-Password (16 Zeichen)</Label>
              <Input
                id="ci-pwd"
                type="password"
                placeholder={cfg?.configured ? "Leer lassen = bestehendes Passwort behalten" : "xxxx xxxx xxxx xxxx"}
                value={form.app_password}
                onChange={(e) => setForm({ ...form, app_password: e.target.value })}
                data-testid="central-inbox-password-input"
              />
            </div>
            <div>
              <Label htmlFor="ci-label">Gmail-Label / Ordner (optional)</Label>
              <Input
                id="ci-label"
                placeholder="leer = ganze INBOX"
                value={form.label_filter}
                onChange={(e) => setForm({ ...form, label_filter: e.target.value })}
                data-testid="central-inbox-label-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="ci-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-4 w-4"
                data-testid="central-inbox-active-toggle"
              />
              <Label htmlFor="ci-active" className="text-sm cursor-pointer">
                Automatisches Polling alle 5 Minuten aktiv
              </Label>
            </div>
          </div>

          {testResult && (
            <div
              className={`rounded-md border p-2 text-xs flex items-start gap-1.5 ${
                testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                              : "bg-rose-50 border-rose-200 text-rose-900"
              }`}
              data-testid="central-inbox-test-result"
            >
              {testResult.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <XCircle className="h-4 w-4 mt-0.5" />}
              <span>{testResult.detail}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTest}
              disabled={testing || !form.email_address || !form.app_password}
              data-testid="central-inbox-test-btn"
            >
              {testing && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              <Send className="h-3.5 w-3.5 mr-1.5" /> IMAP + SMTP testen
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !form.email_address}
              data-testid="central-inbox-save-btn"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              {cfg?.configured ? "Aktualisieren" : "Central Inbox aktivieren"}
            </Button>
            {cfg?.configured && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePollNow}
                  disabled={polling}
                  data-testid="central-inbox-poll-now-btn"
                >
                  {polling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />}
                  Jetzt abrufen
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUnlink}
                  className="text-rose-700 hover:bg-rose-50"
                  data-testid="central-inbox-unlink-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Config löschen
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
